import React, { createContext, useContext, useState } from "react";
import useFetchMedia from "../API/useGetMedia";
import useFetchData from "../API/useFetchData";

// Create the context
const PhotosContext = createContext();

// Provide photos and setters to access and modify them
export const PhotosProvider = ({ children }) => {
  const [photos, setPhotos] = useState([]);
  const [Entered, setIsEntered] = useState(false); // New boolean variable
  const [originalImages, setOriginalImages] = useState([]); // Original images state
  const [originalImagesLogo, setOriginalImagesLogo] = useState([]);
  const [acceptedPhotos, setAcceptedPhotos] = useState([]); // Accepted photos state
  const [toApprovePhotos, setToApprovePhotos] = useState([]); // To approve photos state

  const [allFrames, setAllFrames] = useState([]);
  const [loadingFrames, setLoading] = useState(false);

  const { fetchMedia } = useFetchMedia();
  const { fetchData } = useFetchData("/manager/vm/photo/parent-assets");

  const getFrameMedia = async (frame, asset) => {
    const { type, link, id, margin_x0, margin_y0, margin_x1, margin_y1 } =
      asset;
    try {
      const media = await fetchMedia(link);
      frame.filters[type] = {
        id,
        image: media.url,
        margin_x0,
        margin_y0,
        margin_x1,
        margin_y1,
      };
    } catch (error) {
      console.log(`Error fetching media for ${link}:`, error);
    }
  };

  const fetchFrames = async () => {
    if (allFrames.length !== 0 || loadingFrames) return;

    setLoading(true);
    const response = await fetchData();
    if (!response) {
      setLoading(false);
      return;
    }
    const initialFrames = [];

    const framePromises = response.map(async (item) => {
      const frame = { id: item.id, filters: {} };
      const asset = item.assets.find((a) => a.type === "preview");
      await getFrameMedia(frame, asset);
      initialFrames.push(frame);
    });
    await Promise.all(framePromises);
    setAllFrames(initialFrames);
    setLoading(false);

    // Second pass: fetch remaining frames in the background
    const updatePromises = response.map(async (item) => {
      const frameToUpdate = initialFrames.find((f) => f.id === item.id);
      const mediaPromises = item.assets
        .filter((asset) =>
          ["portrait", "landscape", "square"].includes(asset.type)
        )
        .map((asset) => getFrameMedia(frameToUpdate, asset));

      await Promise.all(mediaPromises);
    });
    await Promise.all(updatePromises);
    setAllFrames(initialFrames);
  };

  return (
    <PhotosContext.Provider
      value={{
        photos,
        setPhotos,
        Entered,
        setIsEntered,
        originalImages,
        setOriginalImages,
        originalImagesLogo,
        setOriginalImagesLogo,
        acceptedPhotos,
        setAcceptedPhotos,
        toApprovePhotos,
        setToApprovePhotos,
        allFrames,
        fetchFrames,
        loadingFrames,
      }}
    >
      {children}
    </PhotosContext.Provider>
  );
};

// Custom hook to use photos context
export const usePhotos = () => useContext(PhotosContext);
